import type { ReactNode } from 'react';

import { centsToFormattedDollars, parseStringProperties } from '@pesto/utils';
import { z } from 'zod';

import { Typography } from '../components/Typography';

export const colorizeResult = (value: string | boolean | undefined | null) => {
  switch (value) {
    case true:
      return (
        <Typography variant={'body'} className={'text-success'}>
          true
        </Typography>
      );
    case false:
      return (
        <Typography variant={'body'} className={'text-destructive'}>
          false
        </Typography>
      );
    case null:
    case undefined:
      return (
        <Typography variant={'body'} className={'text-muted-foreground'}>
          n/a
        </Typography>
      );
    default:
      return (
        <Typography variant={'body'} className={'text-primary'}>
          {value}
        </Typography>
      );
  }
};

function parseNumberProperties(key: string, value: number) {
  switch (key) {
    case 'creditValue':
    case 'valuation':
      return centsToFormattedDollars(value);
    default:
      return value;
  }
}

const recordSchema = z.record(z.unknown());

export const displayValueSchema = z.union([
  z.boolean().transform((value) => ({ kind: 'boolean' as const, value })),
  z.union([z.number(), z.nan()]).transform((value) => ({ kind: 'number' as const, value })),
  z
    .object({ type: z.literal('component'), component: z.custom<ReactNode>() })
    .transform(({ component }) => ({ kind: 'component' as const, value: component })),
  z
    .array(z.union([recordSchema, z.array(z.unknown())]))
    .transform((value) => ({ kind: 'nested-list' as const, value })),
  z.array(z.unknown()).transform((value) => ({ kind: 'list' as const, value })),
  recordSchema.transform((value) => ({ kind: 'record' as const, value })),
  z.any().transform((value) => ({ kind: 'text' as const, value })),
]);

export function formatApplicationValues(key: string, value: any) {
  const displayValue = displayValueSchema.parse(value);

  if (displayValue.kind === 'boolean') return colorizeResult(displayValue.value);

  if (displayValue.kind === 'number') return parseNumberProperties(key, displayValue.value);

  if (displayValue.kind === 'component') return displayValue.value;

  return parseStringProperties(key, value);
}
